/**
 * Approvals — Capex approval queue.
 *
 * Cross-deal queue of pending OFFER / PAY approvals (sourced from
 * GET /api/broker_deals/approvals/pending). A CapexApprover reviews each row
 * and Approves / Rejects it; the decision is written through the existing
 * approval update endpoint (which auto-stamps approver + date server-side),
 * then the list refreshes.
 *
 * Gating: the nav slot + this route are only shown when
 * `can("approvalQueue.view")` (BD.CapexApprover, plus the superuser /
 * transition-fallback rules in permissions.js). Defense-in-depth for the UI:
 * if a user reaches /approvals without the permission (e.g. a stale
 * bookmark), redirect to the deals list. This is still UI/UX only — the
 * de-hub API is the real boundary.
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Popconfirm,
  Segmented,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router";

import { WigAntTable } from "@warwick-investment-group/wig-react-library";

import SignIn from "../signIn";
import { AppContext } from "../../AppContextProvider";
import usePermissions from "../../hooks/usePermissions";
import dealsService from "../../services/dealsService";

const { Text } = Typography;

// OFFER/PAY filter options. "ALL" sends no approval_type param.
const TYPE_FILTERS = [
  { label: "All", value: "ALL" },
  { label: "Offer", value: "OFFER" },
  { label: "Pay", value: "PAY" },
];

const TYPE_COLOR = { OFFER: "blue", PAY: "purple" };

// UAT: newly submitted OFFER / PAY rows only showed up after the approver
// reloaded the browser, because the queue fetched on mount and never again.
// Refresh in the background on this cadence (matched to NotificationBell so
// the badge and the queue stay in step) and on tab focus, plus an explicit
// Refresh control on the card.
const POLL_MS = 45000;

// Format an ISO-ish timestamp to a plain date; leave blanks muted.
const fmtDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
};

const Approvals = () => {
  const navigate = useNavigate();
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  const { can } = usePermissions();
  const allowed = can("approvalQueue.view");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState(null);
  const [typeFilter, setTypeFilter] = useState("ALL");
  // Per-row pending decision state, keyed by approval id, so only the row
  // being acted on shows a spinner.
  const [pendingId, setPendingId] = useState(null);

  useEffect(() => {
    if (!allowed) navigate("/", { replace: true });
  }, [allowed, navigate]);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  // silent = background refresh (poll / tab focus): skip the spinner and
  // the error banner so the table never flashes or throws a red alert under
  // the approver mid-decision. Foreground loads (mount, filter change,
  // Refresh button, post-decision) keep both.
  const refresh = useCallback(
    async (silent = false) => {
      if (!allowed) return;
      try {
        if (!silent) {
          setLoading(true);
          setErrMsg(null);
        }
        const { token: tk, userOid } = await getAuth();
        const resp = await dealsService.listPendingApprovals({
          token: tk,
          userOid,
          approvalType: typeFilter === "ALL" ? undefined : typeFilter,
        });
        setRows(resp?.data?.data || []);
      } catch (err) {
        console.error("Approval queue load failed:", err);
        if (!silent) setErrMsg(err?.message || String(err));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [allowed, getAuth, typeFilter]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Keep the latest refresh reachable from the poll / focus listeners without
  // making them re-subscribe every time the token or filter changes identity.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  // Dedupe overlapping background fetches (focus + visibilitychange fire
  // together) and never refetch while a decision is in flight — swapping the
  // rows out from under the row being acted on loses the spinner.
  const bgBusyRef = useRef(false);
  const pendingRef = useRef(null);
  pendingRef.current = pendingId;

  useEffect(() => {
    if (!allowed) return undefined;
    const background = async () => {
      if (bgBusyRef.current || pendingRef.current !== null) return;
      if (document.visibilityState === "hidden") return;
      bgBusyRef.current = true;
      try {
        await refreshRef.current(true);
      } finally {
        bgBusyRef.current = false;
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") background();
    };
    const id = setInterval(background, POLL_MS);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [allowed]);

  const decide = useCallback(
    async (row, decision) => {
      try {
        setPendingId(row.id);
        const { token: tk, userOid } = await getAuth();
        await dealsService.updateApproval({
          token: tk,
          userOid,
          id: row.id,
          // `deal_id` is immutable on ApprovalUpdateRequest (extra="forbid"),
          // so sending it 422s the PUT. Status + type only — the server
          // auto-stamps approver / approved_at on a terminal decision.
          body: {
            approval_status: decision,
            approval_type: row.approval_type,
          },
        });
        message.success(
          `${row.approval_type} approval ${
            decision === "APPROVED" ? "approved" : "rejected"
          }`
        );
        await refresh();
      } catch (err) {
        console.error("Approval decision failed:", err);
        message.error(err?.message || "Failed to record decision");
      } finally {
        setPendingId(null);
      }
    },
    [getAuth, refresh]
  );

  const columns = useMemo(
    () => [
      {
        title: "Deal",
        dataIndex: "deal_key",
        key: "deal_key",
        sorter: (a, b) =>
          String(a?.deal_key ?? "").localeCompare(String(b?.deal_key ?? "")),
        render: (value) =>
          value ? (
            <Link to={`/deals/${encodeURIComponent(value)}`}>{value}</Link>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: "Type",
        dataIndex: "approval_type",
        key: "approval_type",
        sorter: (a, b) =>
          String(a?.approval_type ?? "").localeCompare(
            String(b?.approval_type ?? "")
          ),
        render: (value) => (
          <Tag color={TYPE_COLOR[value] || "default"}>{value}</Tag>
        ),
      },
      {
        title: "Notes",
        dataIndex: "approval_notes",
        key: "approval_notes",
        sorter: (a, b) =>
          String(a?.approval_notes ?? "").localeCompare(
            String(b?.approval_notes ?? "")
          ),
        render: (value) =>
          value ? value : <Text type="secondary">—</Text>,
      },
      {
        // Newest-first by default. The API orders PENDING rows by deal_id
        // (approved_at is null until a decision), so a freshly submitted
        // request on a high deal id landed at the bottom - and past 25 rows,
        // on page 2, which reads as "my submission never showed up".
        title: "Submitted",
        dataIndex: "created_at",
        key: "created_at",
        defaultSortOrder: "descend",
        sorter: (a, b) =>
          new Date(a.created_at || 0) - new Date(b.created_at || 0),
        render: (value) => fmtDate(value),
      },
      {
        title: "Decision",
        key: "decision",
        width: 200,
        render: (_, row) => (
          <Space>
            <Button
              size="small"
              type="primary"
              icon={<CheckOutlined />}
              loading={pendingId === row.id}
              onClick={() => decide(row, "APPROVED")}
            >
              Approve
            </Button>
            <Popconfirm
              title="Reject this approval?"
              okText="Reject"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              onConfirm={() => decide(row, "REJECTED")}
            >
              <Button
                size="small"
                danger
                icon={<CloseOutlined />}
                loading={pendingId === row.id}
              >
                Reject
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [decide, pendingId]
  );

  return (
    <React.Fragment>
      <AuthenticatedTemplate>
        <div style={{ padding: 16, width: "99vw", boxSizing: "border-box" }}>
          {allowed && (
            <Card
              size="small"
              title="Approval Queue"
              extra={
                <Space>
                  <Segmented
                    size="small"
                    options={TYPE_FILTERS}
                    value={typeFilter}
                    onChange={setTypeFilter}
                  />
                  {/* Explicit escape hatch: the queue also polls and */}
                  {/* refetches on tab focus, but an approver who just */}
                  {/* asked someone to submit should not have to wait. */}
                  {/* Wrapped so the click event is not passed as silent. */}
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    loading={loading}
                    onClick={() => refresh()}
                  >
                    Refresh
                  </Button>
                </Space>
              }
            >
              {errMsg && (
                <Alert
                  type="error"
                  showIcon
                  message="Failed to load approvals"
                  description={errMsg}
                  style={{ marginBottom: 12 }}
                />
              )}
              <WigAntTable
                rowKey={(r) => r.id}
                columns={columns}
                dataSource={rows}
                loading={loading}
                size="small"
                showSearch={false}
                showExport={false}
                showActionButton={false}
                pagination={{ pageSize: 100, hideOnSinglePage: true }}
                locale={{
                  emptyText: (
                    <Empty
                      description={
                        <Text type="secondary">No pending approvals</Text>
                      }
                    />
                  ),
                }}
              />
            </Card>
          )}
        </div>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
    </React.Fragment>
  );
};

export default Approvals;
